// Auth/session store: the JWT returned by POST /auth/login (FD §7) persisted
// in localStorage, decoded (client-side, display purposes only — the API is
// the actual authority and re-verifies on every request) into the current
// user, plus an AuthProvider so route components/guards can read the current
// user and call login()/logout() without prop-drilling.
//
// TODO: replace localStorage with an httpOnly cookie-based session once
// packages/api/src/plugins/session.ts is wired in — a JWT sitting in
// localStorage is XSS-exposed and is only acceptable at this scaffold stage.

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { JWTPayload, UserRoleName } from '@goblic/shared'
import { queryClient } from './query-client'

const TOKEN_STORAGE_KEY = 'goblic.auth.token'

export interface AuthenticatedUser {
  id: string
  workspaceId: string
  roles: UserRoleName[]
  /** Not carried by the JWT payload itself; populated once /auth/me exists. */
  email?: string
  /**
   * Installation administrator, from the token's `instanceAdmin` claim — a
   * hint only: tokens from before migration 0030 lack it, so the menu and the
   * Installation pages read `GET /me` instead (sandbox-hosts ontwerp §15.1
   * item 10). The API re-checks on every request.
   */
  isInstanceAdmin: boolean
}

export interface AuthState {
  user: AuthenticatedUser | null
  token: string | null
}

export interface AuthContextValue extends AuthState {
  /** Persists `token` and updates the decoded user in one step. */
  login: (token: string) => void
  /** Clears the persisted token and the current user. */
  logout: () => void
}

/** Reads the persisted JWT, if any. Never throws (private browsing, quota, ...). */
export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

/** Persists (or clears, when `token` is null) the JWT. Never throws. */
export function setAuthToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token)
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY)
    }
  } catch {
    // TODO: surface a warning when storage is unavailable instead of
    // silently failing (private mode, quota, blocked site data).
  }
}

/**
 * Makes `token` (for another workspace) the active one and reloads the app at
 * `redirect`: the hard reload closes SSE/WebSocket connections and resets all
 * state, so nothing of the previous workspace lingers.
 */
export function switchToToken(token: string, redirect: string): void {
  setAuthToken(token)
  queryClient.clear()
  window.location.assign(redirect)
}

/**
 * Decodes a JWT's payload for display purposes only — this does NOT verify
 * the signature (the browser has no way to; that's the API's job on every
 * request). Returns null for anything that doesn't parse as the expected
 * shape rather than throwing, since a corrupt/foreign value in localStorage
 * must never crash the app.
 */
export function decodeToken(token: string): AuthenticatedUser | null {
  try {
    const payloadSegment = token.split('.')[1]
    if (!payloadSegment) return null
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((char) => '%' + char.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    )
    const payload = JSON.parse(json) as Partial<JWTPayload>
    if (!payload.sub || !payload.workspaceId || !Array.isArray(payload.roles))
      return null
    if (payload.exp != null && payload.exp * 1000 < Date.now()) return null
    return {
      id: payload.sub,
      workspaceId: payload.workspaceId,
      roles: payload.roles,
      isInstanceAdmin: payload.instanceAdmin === true,
    }
  } catch {
    return null
  }
}

/** The workspace the stored token is bound to (display purposes only). */
export function getAuthWorkspaceId(): string | null {
  const token = getAuthToken()
  return token ? (decodeToken(token)?.workspaceId ?? null) : null
}

export function isAuthenticated(): boolean {
  const token = getAuthToken()
  return token != null && decodeToken(token) != null
}

/** The stored token's user, outside React (route guards). Null when signed out. */
export function currentUser(): AuthenticatedUser | null {
  const token = getAuthToken()
  return token ? decodeToken(token) : null
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  token: null,
  login: () => {},
  logout: () => {},
})

export function useAuth(): AuthContextValue {
  return useContext(AuthContext)
}

/** Convenience accessor for the handful of pages that only need the workspace id. */
export function useWorkspaceId(): string {
  return useAuth().user?.workspaceId ?? ''
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getAuthToken())
  const [user, setUser] = useState<AuthenticatedUser | null>(() => {
    const initial = getAuthToken()
    return initial ? decodeToken(initial) : null
  })

  const userRef = useRef(user)
  userRef.current = user

  // Keeps tabs in sync when the token changes in another tab (e.g. logging
  // out there), and clears state if the token in storage disappears/expires.
  useEffect(() => {
    function onStorage(event: StorageEvent): void {
      if (event.key !== null && event.key !== TOKEN_STORAGE_KEY) return
      const current = getAuthToken()
      // Another tab switched workspace: this tab's pages belong to the old one.
      const next = current ? decodeToken(current) : null
      if (next && next.workspaceId !== userRef.current?.workspaceId && userRef.current) {
        queryClient.clear()
        window.location.assign('/')
        return
      }
      setToken(current)
      setUser(current ? decodeToken(current) : null)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const login = useCallback((newToken: string) => {
    queryClient.clear()
    setAuthToken(newToken)
    setToken(newToken)
    setUser(decodeToken(newToken))
  }, [])

  const logout = useCallback(() => {
    queryClient.clear()
    setAuthToken(null)
    setToken(null)
    setUser(null)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, login, logout }),
    [user, token, login, logout]
  )

  return createElement(AuthContext.Provider, { value }, children)
}
